import React, { useCallback, useEffect, useState } from "react";
import { toggleSession } from "@react-three/xr";

const buttonStyles = {
  position: "fixed",
  bottom: "24px",
  left: "50%",
  transform: "translateX(-50%)",
  padding: "12px 24px",
  border: "1px solid white",
  borderRadius: "4px",
  background: "rgba(0, 0, 0, 0.1)",
  color: "white",
  font: "normal 0.8125rem sans-serif",
  outline: "none",
  zIndex: 99999,
  cursor: "pointer",
};

const EMULATOR_HINT =
  "On desktop: open Chrome DevTools → the WebXR / Immersive Web Emulator panel, pick a device, click Start emulating, then enter VR again (you may need to refresh once after starting emulation).";

/**
 * DOM-only: must be a sibling of the R3F Canvas, not a child (R3F would try to create THREE nodes).
 * Pass isPresenting from PreviewVRSessionSync inside the XR provider.
 */
export function PreviewVRSessionControls({ isPresenting = false, sessionInit } = {}) {
  const [supported, setSupported] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!navigator?.xr) {
      setSupported(false);
      return;
    }
    navigator.xr
      .isSessionSupported("immersive-vr")
      .then(setSupported)
      .catch(() => setSupported(false));
  }, []);

  const handleClick = useCallback(async () => {
    setError(null);
    try {
      await toggleSession("immersive-vr", { sessionInit });
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      setError(message);
      console.warn("WebXR session error:", e);
    }
  }, [sessionInit]);

  if (supported === false) {
    return (
      <div
        style={{
          ...buttonStyles,
          cursor: "default",
          maxWidth: 440,
          textAlign: "center",
          lineHeight: 1.45,
        }}
      >
        <div>VR is not available in this browser (no WebXR API).</div>
        <div style={{ marginTop: 10, fontSize: 11, opacity: 0.9 }}>{EMULATOR_HINT}</div>
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        style={{
          ...buttonStyles,
          opacity: supported === null ? 0.65 : 1,
          pointerEvents: supported === null ? "none" : "auto",
        }}
        disabled={supported === null}
        onClick={handleClick}
      >
        {isPresenting ? "Exit VR" : "Enter VR"}
      </button>
      {error ? (
        <div
          style={{
            position: "fixed",
            bottom: 72,
            left: "50%",
            transform: "translateX(-50%)",
            maxWidth: 480,
            padding: "12px 16px",
            background: "rgba(30, 10, 10, 0.95)",
            color: "#fecaca",
            fontSize: 12,
            lineHeight: 1.45,
            borderRadius: 6,
            zIndex: 99999,
            textAlign: "center",
            border: "1px solid rgba(248, 113, 113, 0.35)",
          }}
        >
          <div>{error}</div>
          <div style={{ marginTop: 10, color: "#fca5a5", fontSize: 11 }}>{EMULATOR_HINT}</div>
        </div>
      ) : null}
    </>
  );
}
